import React, { createContext, useContext, useEffect, useState } from "react";
import { auth, db } from "../firebase";
import {
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  createUserWithEmailAndPassword,
} from "firebase/auth";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (u) => {
      setUser(u);
      if (u) {
        // Fetch user profile from Firestore
        const ref = doc(db, "users", u.uid);
        const snap = await getDoc(ref);
        if (snap.exists()) setUserProfile(snap.data());
        else setUserProfile(null);
      } else {
        setUserProfile(null);
      }
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  const login = async (email, password) => {
    const cred = await signInWithEmailAndPassword(auth, email, password);
    // Fetch profile after login
    const ref = doc(db, "users", cred.user.uid);
    const snap = await getDoc(ref);
    if (snap.exists()) setUserProfile(snap.data());
    return cred;
  };

  const register = async (email, password, name) => {
    const cred = await createUserWithEmailAndPassword(auth, email, password);
    // Save user to Firestore with pending status
    await setDoc(doc(db, "users", cred.user.uid), {
      uid: cred.user.uid,
      email,
      name,
      status: "pending", // admin must approve
      role: "member",
      createdAt: serverTimestamp(),
    });
    setUserProfile({ email, name, status: "pending", role: "member" });
    return cred;
  };

  const logout = () => {
    setUserProfile(null);
    return signOut(auth);
  };

  const isAdmin = user && user.email === process.env.REACT_APP_ADMIN_EMAIL;
  const isApproved = userProfile && userProfile.status === "approved";

  return (
    <AuthContext.Provider value={{ user, userProfile, loading, login, logout, register, isAdmin, isApproved }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
